import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserMinus, Download, Phone, Sparkles } from 'lucide-react';
import { getLost, LostResp, LostRow } from '../api';
import { getWhatsAppLink } from '../../../utils/whatsapp';
import { Skeleton } from '../../../components/Skeleton';
import { readCache, writeCache } from '../../../services/localCache';
import CountUp from '../../../components/CountUp';
import EmptyState from '../../../components/EmptyState';

const PERIODS = [
  { v: '90', label: 'Últimos 90 días' },
  { v: '180', label: '6 meses' },
  { v: '365', label: '1 año' },
  { v: 'all', label: 'Todas' },
];

function fullName(r: LostRow) { return `${r.firstname || ''} ${r.lastname || ''}`.trim(); }
function money(n: number) { return '$' + Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 }); }
function fmtDate(d: string | null) {
  if (!d) return '—';
  return new Date(d.slice(0, 10) + 'T12:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
}
function waLink(r: LostRow) {
  const msg = `Hola ${r.firstname || ''} 💖 Te extrañamos en Let's Move. Nos encantaría verte de regreso en clase. ¿Te animas?`;
  return getWhatsAppLink(r.phone_prefix, r.phone_number, msg) || null;
}

function exportCsv(r: LostResp) {
  const lines = ['Alumno,Teléfono,Última clase,Última asistencia,Días sin asistir,Valor histórico,Pagos'];
  for (const x of r.data) {
    lines.push([
      `"${fullName(x)}"`, `${x.phone_prefix || ''}${x.phone_number || ''}`,
      `"${x.last_class_name || ''}"`, x.last_attendance || '',
      x.days_since ?? '', x.ltv, x.payments_count,
    ].join(','));
  }
  const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `bajas-letsmove-${r.days}.csv`; a.click();
  URL.revokeObjectURL(url);
}

export default function LostReport() {
  // Pinta al instante lo último conocido por rango (caché local) y refresca por detrás.
  const [data, setData] = useState<LostResp | null>(() => readCache<LostResp>('rep-lost:all') ?? null);
  const [days, setDays] = useState('all');
  const [loading, setLoading] = useState(() => !readCache('rep-lost:all'));

  useEffect(() => {
    const cached = readCache<LostResp>(`rep-lost:${days}`);
    if (cached) { setData(cached); setLoading(false); } else setLoading(true);
    getLost(days).then((r) => { setData(r); writeCache(`rep-lost:${days}`, r); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [days]);

  return (
    <div className="space-y-4">
      {/* Controles */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm text-content-3">
          <span>Última asistencia</span>
          <div className="flex flex-wrap rounded-lg border border-line bg-surface p-0.5">
            {PERIODS.map((d) => (
              <button key={d.v} onClick={() => setDays(d.v)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                  d.v === days ? 'bg-accent text-on-accent' : 'text-content-2 hover:bg-surface-2'}`}>
                {d.label}
              </button>
            ))}
          </div>
        </div>
        {data && data.data.length > 0 && (
          <button onClick={() => exportCsv(data)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-content-2 hover:bg-surface-2">
            <Download size={15} /> CSV
          </button>
        )}
      </div>

      {loading || !data ? (
        <Skeleton className="h-96 w-full rounded-2xl" />
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
              <div className="text-xs font-medium uppercase tracking-wide text-content-3">Bajas</div>
              <div className="mt-1 font-display text-2xl font-bold text-content"><CountUp value={data.summary.lost} /></div>
              <div className="mt-1 text-xs text-content-3">ex-alumnos</div>
            </div>
            <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
              <div className="text-xs font-medium uppercase tracking-wide text-content-3">Con teléfono</div>
              <div className="mt-1 font-display text-2xl font-bold text-content"><CountUp value={data.summary.with_phone} /></div>
              <div className="mt-1 text-xs text-content-3">contactables por WhatsApp</div>
            </div>
            <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
              <div className="text-xs font-medium uppercase tracking-wide text-content-3">Valor histórico</div>
              <div className="mt-1 font-display text-2xl font-bold text-brand-blue"><CountUp value={data.summary.total_value} format={(n) => money(n)} /></div>
              <div className="mt-1 text-xs text-content-3">que pagaron en su día</div>
            </div>
            <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
              <div className="text-xs font-medium uppercase tracking-wide text-content-3">Recientes</div>
              <div className="mt-1 font-display text-2xl font-bold text-content"><CountUp value={data.summary.recent} /></div>
              <div className="mt-1 text-xs text-content-3">se fueron hace ≤90 días</div>
            </div>
          </div>

          <p className="flex items-center gap-1.5 text-xs text-content-3">
            <Sparkles size={13} className="text-accent-ink" />
            Ordenadas por valor histórico: las de arriba son las más valiosas por recuperar.
          </p>

          {data.data.length === 0 ? (
            <EmptyState icon={UserMinus} title="Sin bajas en el periodo" subtitle="No hay ex-alumnos con última asistencia en ese rango." />
          ) : (
            <div className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
              {/* Tabla (escritorio) */}
              <table className="hidden w-full text-sm sm:table">
                <thead>
                  <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-content-3">
                    <th className="px-4 py-3 font-medium">Alumno</th>
                    <th className="px-4 py-3 font-medium">Última clase</th>
                    <th className="px-4 py-3 font-medium">Última asistencia</th>
                    <th className="px-4 py-3 text-right font-medium">Valor histórico</th>
                    <th className="px-4 py-3 text-center font-medium">Contacto</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((r) => {
                    const wa = waLink(r);
                    return (
                      <tr key={r.account_id} className="border-b border-line/60 last:border-0 even:bg-surface-2/40 hover:bg-surface-2/70">
                        <td className="px-4 py-2.5">
                          <Link to={`/alumnos/${r.account_id}`} className="flex items-center gap-2.5 hover:underline">
                            {r.has_photo ? (
                              <img src={`/api/letsmove/members/${r.account_id}/photo`} alt="" className="h-8 w-8 rounded-full object-cover" loading="lazy" />
                            ) : (
                              <span className="grid h-8 w-8 place-items-center rounded-full bg-surface-2 text-xs font-medium text-content-3">
                                {(r.firstname?.[0] || '?').toUpperCase()}
                              </span>
                            )}
                            <span className="font-medium text-content">{fullName(r)}</span>
                          </Link>
                        </td>
                        <td className="px-4 py-2.5 text-content-2">{r.last_class_name || '—'}</td>
                        <td className="px-4 py-2.5 text-content-2">
                          {fmtDate(r.last_attendance)}
                          {r.days_since !== null && (
                            <span className={`ml-1.5 text-xs ${r.days_since <= 90 ? 'text-amber-600 dark:text-amber-400' : 'text-content-3'}`}>
                              ({r.days_since}d)
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right font-medium text-content">{money(r.ltv)}</td>
                        <td className="px-4 py-2.5 text-center">
                          {wa ? (
                            <a href={wa} target="_blank" rel="noreferrer"
                              className="inline-flex items-center gap-1 rounded-md border border-line px-2 py-1 text-xs font-medium text-emerald-600 hover:bg-surface-2 dark:text-emerald-400"
                              title="Escribir por WhatsApp">
                              <Phone size={12} /> WhatsApp
                            </a>
                          ) : <span className="text-xs text-content-3">sin tel.</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Tarjetas (móvil) */}
              <ul className="divide-y divide-line/60 sm:hidden">
                {data.data.map((r) => {
                  const wa = waLink(r);
                  return (
                    <li key={r.account_id} className="p-3">
                      <div className="flex items-start justify-between gap-3">
                        <Link to={`/alumnos/${r.account_id}`} className="flex min-w-0 items-center gap-2.5">
                          {r.has_photo ? (
                            <img src={`/api/letsmove/members/${r.account_id}/photo`} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" loading="lazy" />
                          ) : (
                            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-surface-2 text-xs font-medium text-content-3">
                              {(r.firstname?.[0] || '?').toUpperCase()}
                            </span>
                          )}
                          <span className="min-w-0">
                            <span className="block truncate font-medium text-content">{fullName(r)}</span>
                            <span className="block truncate text-xs text-content-3">{r.last_class_name || 'Sin última clase'}</span>
                          </span>
                        </Link>
                        <span className="shrink-0 text-right">
                          <span className="block font-display font-bold text-brand-blue">{money(r.ltv)}</span>
                          <span className="block text-[11px] text-content-3">histórico</span>
                        </span>
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <span className="text-xs text-content-3">
                          Última asist.: {fmtDate(r.last_attendance)}
                          {r.days_since !== null && (
                            <span className={`ml-1 ${r.days_since <= 90 ? 'text-amber-600 dark:text-amber-400' : ''}`}>({r.days_since}d)</span>
                          )}
                        </span>
                        {wa ? (
                          <a href={wa} target="_blank" rel="noreferrer"
                            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-line px-2 py-1 text-xs font-medium text-emerald-600 hover:bg-surface-2 dark:text-emerald-400">
                            <Phone size={12} /> WhatsApp
                          </a>
                        ) : <span className="shrink-0 text-xs text-content-3">sin tel.</span>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
